/**
 * Hidden Power calculation utilities for frontend
 * Reference: https://bulbapedia.bulbagarden.net/wiki/Hidden_Power_(move)/Calculation
 */

// Hidden Power type mapping
const HIDDEN_POWER_TYPES = [
  'Fighting', 'Flying', 'Poison', 'Ground', 'Rock', 'Bug', 'Ghost', 'Steel',
  'Fire', 'Water', 'Grass', 'Electric', 'Psychic', 'Ice', 'Dragon', 'Dark'
];

export interface HiddenPowerResult {
  type: string;
  power: number;
}

export interface IVStats {
  hp?: number;
  attack?: number;
  defense?: number;
  spAttack?: number;
  spDefense?: number;
  speed?: number;
}

/**
 * Calculate Hidden Power type and power from IVs
 * @param ivs - IV stats object
 * @returns Hidden Power type and power, or null if insufficient IVs
 */
export const calculateHiddenPower = (ivs: IVStats): HiddenPowerResult | null => {
  // Check if we have all required IVs
  if (
    ivs.hp === undefined || ivs.hp === null ||
    ivs.attack === undefined || ivs.attack === null ||
    ivs.defense === undefined || ivs.defense === null ||
    ivs.spAttack === undefined || ivs.spAttack === null ||
    ivs.spDefense === undefined || ivs.spDefense === null ||
    ivs.speed === undefined || ivs.speed === null
  ) {
    return null;
  }

  // Validate IV range (0-31)
  const allIVs = [ivs.hp, ivs.attack, ivs.defense, ivs.spAttack, ivs.spDefense, ivs.speed];
  if (allIVs.some(iv => iv < 0 || iv > 31)) {
    return null;
  }

  // Calculate Hidden Power Type
  // Type bits are the least significant bit (LSB) of each IV
  const typeBits = {
    a: ivs.hp % 2,        // HP
    b: ivs.attack % 2,    // Attack
    c: ivs.defense % 2,   // Defense
    d: ivs.speed % 2,     // Speed
    e: ivs.spAttack % 2,  // Special Attack
    f: ivs.spDefense % 2  // Special Defense
  };

  // HP_type = floor((a + 2b + 4c + 8d + 16e + 32f) × 15/63)
  const typeSum = typeBits.a + (2 * typeBits.b) + (4 * typeBits.c) +
                  (8 * typeBits.d) + (16 * typeBits.e) + (32 * typeBits.f);
  const typeIndex = Math.floor((typeSum * 15) / 63);
  const type = HIDDEN_POWER_TYPES[typeIndex];

  // Calculate Hidden Power Power
  // Power bits are the second least significant bit of each IV
  const powerBits = {
    u: Math.floor(ivs.hp % 4 / 2),        // HP
    v: Math.floor(ivs.attack % 4 / 2),    // Attack
    w: Math.floor(ivs.defense % 4 / 2),   // Defense
    x: Math.floor(ivs.speed % 4 / 2),     // Speed
    y: Math.floor(ivs.spAttack % 4 / 2),  // Special Attack
    z: Math.floor(ivs.spDefense % 4 / 2)  // Special Defense
  };

  // HP_power = floor((u + 2v + 4w + 8x + 16y + 32z) × 40/63) + 30
  const powerSum = powerBits.u + (2 * powerBits.v) + (4 * powerBits.w) +
                   (8 * powerBits.x) + (16 * powerBits.y) + (32 * powerBits.z);
  const power = Math.floor((powerSum * 40) / 63) + 30;

  return {
    type,
    power
  };
};

/**
 * Format Hidden Power result as a string (type only, power is always 60)
 * @param hiddenPower - Hidden Power calculation result
 * @returns Formatted string like "Fire" or null if no result
 */
export const formatHiddenPower = (hiddenPower: HiddenPowerResult | null): string | null => {
  if (!hiddenPower) return null;
  return hiddenPower.type; // Only return type, power is always 60
};
